import React, { useEffect, useMemo, useState } from "react";
import { api, brl } from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, Trash2, MessageCircle, Search, RefreshCw, Star, Power,
  Sparkles, Wand2, Send, BookOpen, Edit2, X, Brain, ChevronDown,
} from "lucide-react";

const onlyDigits = (s) => (s || "").replace(/\D/g, "");
/**
 * Constrói URL wa.me normalizando o telefone:
 *   - Se o número original começa com "+" (qualquer país, ex.: +1 555…, +44 207…),
 *     usa os dígitos como estão (preserva DDI internacional).
 *   - Se NÃO tem "+", assume Brasil — prepende 55 se ainda não estiver presente.
 *   - Se já vier com "55" no início e mais que 11 dígitos, mantém.
 */
const waUrl = (phone, msg) => {
  const raw = (phone || "").trim();
  if (!raw) return null;
  const d = onlyDigits(raw);
  if (!d) return null;
  let full;
  if (raw.startsWith("+")) {
    full = d;                                  // número internacional já tem DDI
  } else if (d.length >= 12 && d.startsWith("55")) {
    full = d;                                  // já é BR com DDI
  } else if (d.length >= 10) {
    full = "55" + d;                           // BR sem DDI (10/11 dígitos)
  } else {
    full = d;
  }
  return `https://wa.me/${full}?text=${encodeURIComponent(msg || "")}`;
};

export default function Comparador() {
  const [tab, setTab] = useState("comparar");
  return (
    <div className="space-y-6" data-testid="comparador-page">
      <div>
        <p className="label-tiny mb-2">Inteligência de preços</p>
        <h1 className="font-display text-4xl tracking-tight">Comparador de fornecedores</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Cole listas bagunçadas — o sistema entende abreviações, agrupa produtos iguais e mostra os melhores preços.
        </p>
      </div>

      <div
        className="rounded-md border border-amber-300 bg-amber-50/70 dark:bg-amber-500/10 px-4 py-3 flex gap-3"
        data-testid="comparador-novo-vs-seminovo-alert"
        role="note"
      >
        <span className="text-amber-600 text-lg leading-none mt-0.5">⚠</span>
        <div className="text-[13px] leading-relaxed text-amber-900 dark:text-amber-200">
          <p className="font-medium mb-0.5">Atenção: produtos novos × seminovos</p>
          <p>
            Verifique se o produto é <strong>novo</strong> ou <strong>seminovo</strong>. Se você carregou
            listas de novos e seminovos no mesmo fornecedor, eles aparecerão juntos na mesma comparação.
          </p>
          <p className="mt-1">
            Para listar separadamente, <strong>cadastre cada fornecedor duas vezes</strong> —
            uma com lista de novos e outra com lista de seminovos.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-neutral-100 dark:bg-white/5 p-1 rounded-md">
          <TabsTrigger value="comparar" data-testid="tab-comparar">Comparar</TabsTrigger>
          <TabsTrigger value="fornecedores" data-testid="tab-fornecedores">Fornecedores</TabsTrigger>
          <TabsTrigger value="aliases" data-testid="tab-aliases">Dicionário</TabsTrigger>
          <TabsTrigger value="enviar" data-testid="tab-enviar">Enviar lista</TabsTrigger>
        </TabsList>

        <TabsContent value="comparar"><ComparePane/></TabsContent>
        <TabsContent value="fornecedores"><SuppliersPane/></TabsContent>
        <TabsContent value="aliases"><AliasesPane/></TabsContent>
        <TabsContent value="enviar"><RecipientsPane/></TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPARE — view agrupada por fornecedor (clique pra expandir cores)
// ─────────────────────────────────────────────────────────────────────────────
function ComparePane() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [minRating, setMinRating] = useState(0);
  const [filterStorage, setFilterStorage] = useState("");
  const [filterModel, setFilterModel] = useState("");
  const [filterColor, setFilterColor] = useState("");
  const [filterCondition, setFilterCondition] = useState("all"); // all|new|used
  const [expanded, setExpanded] = useState({}); // {[`${model}-${supId}`]: bool}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/compare/comparison", { _cache: false });
      setRows(r.data || []);
    } catch (e) { toast.error("Erro ao carregar comparação"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Listas únicas pra filtros
  const opts = useMemo(() => {
    const m = new Set(), s = new Set(), c = new Set();
    rows.forEach(r => {
      if (r.model_full) m.add(r.model_full);
      (r.suppliers || []).forEach(g => (g.items || []).forEach(it => {
        if (it.storage) s.add(it.storage);
        if (it.color) c.add(it.color);
      }));
    });
    return { models: [...m].sort(), storages: [...s].sort(), colors: [...c].sort() };
  }, [rows]);

  // Filtros aplicados
  const filtered = useMemo(() => {
    return rows
      .filter(r => !filterModel || r.model_full === filterModel)
      .map(r => {
        let suppliers = r.suppliers || [];
        // filtros por storage/color/rating dentro dos items do fornecedor
        suppliers = suppliers
          .map(g => {
            let items = g.items || [];
            if (filterStorage) items = items.filter(it => it.storage === filterStorage);
            if (filterColor) items = items.filter(it => it.color === filterColor);
            if (items.length === 0) return null;
            if (minRating > 0 && (g.supplier?.rating || 0) < minRating) return null;
            // Filtro condição (new/used/all). 'all' aceita tudo.
            const cond = g.supplier?.condition || "all";
            if (filterCondition !== "all") {
              // se filtro é "new" → aceita supplier "new" ou "all" (lista mista)
              // se filtro é "used" → aceita "used" ou "all"
              if (cond !== filterCondition && cond !== "all") return null;
            }
            const prices = items.map(it => it.price);
            return {
              ...g,
              items,
              min_price: Math.min(...prices),
              max_price: Math.max(...prices),
              offer_count: items.length,
              total_qty: items.reduce((acc, it) => acc + (it.quantity || 0), 0),
            };
          })
          .filter(Boolean);
        // busca textual
        if (search) {
          const q = search.toLowerCase();
          const headerMatch = (r.model_full || "").toLowerCase().includes(q);
          if (!headerMatch) {
            suppliers = suppliers.filter(g =>
              (g.supplier?.name || "").toLowerCase().includes(q) ||
              (g.items || []).some(it => `${it.storage || ""} ${it.color || ""}`.toLowerCase().includes(q))
            );
          }
        }
        if (suppliers.length === 0) return null;
        suppliers.sort((a, b) => a.min_price - b.min_price);
        return {
          ...r,
          suppliers,
          best_price: suppliers[0].min_price,
          worst_price: Math.max(...suppliers.map(g => g.max_price)),
        };
      })
      .filter(Boolean);
  }, [rows, search, filterModel, filterStorage, filterColor, filterCondition, minRating]);

  const toggle = (k) => setExpanded(p => ({ ...p, [k]: !p[k] }));

  return (
    <div className="space-y-4">
      <Card className="p-3 border-[#E5E5E5] shadow-none flex flex-wrap items-center gap-2" data-testid="compare-toolbar">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
          <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar (modelo, fornecedor, cor…)" className="pl-9" data-testid="compare-search"/>
        </div>
        <select value={filterModel} onChange={(e)=>setFilterModel(e.target.value)} className="text-sm border border-[#E5E5E5] rounded-md px-2 py-1.5 bg-white dark:bg-transparent" data-testid="filter-model">
          <option value="">Todos modelos</option>
          {opts.models.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={filterStorage} onChange={(e)=>setFilterStorage(e.target.value)} className="text-sm border border-[#E5E5E5] rounded-md px-2 py-1.5 bg-white dark:bg-transparent" data-testid="filter-storage">
          <option value="">Todos armaz.</option>
          {opts.storages.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterColor} onChange={(e)=>setFilterColor(e.target.value)} className="text-sm border border-[#E5E5E5] rounded-md px-2 py-1.5 bg-white dark:bg-transparent" data-testid="filter-color">
          <option value="">Todas cores</option>
          {opts.colors.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterCondition} onChange={(e)=>setFilterCondition(e.target.value)} className="text-sm border border-[#E5E5E5] rounded-md px-2 py-1.5 bg-white dark:bg-transparent" data-testid="filter-condition">
          <option value="all">Todos (novos + seminovos)</option>
          <option value="new">Apenas novos</option>
          <option value="used">Apenas seminovos</option>
        </select>
        <select value={minRating} onChange={(e)=>setMinRating(Number(e.target.value))} className="text-sm border border-[#E5E5E5] rounded-md px-2 py-1.5 bg-white dark:bg-transparent" data-testid="filter-rating">
          <option value="0">Nota: qualquer</option>
          <option value="3">3★ ou mais</option>
          <option value="4">4★ ou mais</option>
          <option value="5">Apenas 5★</option>
        </select>
        <Button variant="outline" size="sm" onClick={load} data-testid="compare-refresh"><RefreshCw size={12} className="mr-1"/>Atualizar</Button>
      </Card>

      {loading && <div className="text-center text-sm text-neutral-500 py-10">Carregando…</div>}
      {!loading && filtered.length === 0 && (
        <Card className="p-10 text-center border-[#E5E5E5] shadow-none">
          <Sparkles className="mx-auto text-neutral-400 mb-3" size={32}/>
          <h3 className="font-display text-lg">Sem produtos ainda</h3>
          <p className="text-sm text-neutral-500 mt-1">Cadastre fornecedores e cole suas listas — o sistema processa o resto.</p>
        </Card>
      )}

      {!loading && filtered.map(model => {
        return (
        <Card key={model.model_full} className="p-4 border-[#E5E5E5] shadow-none" data-testid={`model-${model.model_full.replace(/\s+/g, "-")}`}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-display text-xl tracking-tight">{model.model_full}</h3>
              <p className="text-[10px] uppercase tracking-wider text-neutral-500 mt-1">
                {model.suppliers.length} fornecedor(es) · {model.suppliers.reduce((a, g) => a + g.offer_count, 0)} oferta(s)
                {model.total_qty > 0 && <> · {model.total_qty} un. em estoque</>}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-neutral-500">Menor preço</p>
              <p className="font-display text-2xl tracking-tight text-emerald-700 dark:text-emerald-400">{brl(model.best_price)}</p>
              {model.delta > 0 && (
                <p className="text-[10px] text-neutral-500">economia de até <span className="font-medium">{brl(model.delta)}</span></p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            {model.suppliers.map((g, gi) => {
              const sid = g.supplier?.id || `idx-${gi}`;
              const key = `${model.model_full}-${sid}`;
              const open = !!expanded[key];
              const rank = gi; // 0 = 1º
              // 1º verde, 2º amarelo escuro, 3º laranja, demais cinza
              const rankStyle =
                rank === 0 ? "border-emerald-300 bg-emerald-50/40 dark:bg-emerald-500/10" :
                rank === 1 ? "border-amber-400 bg-amber-50/60 dark:bg-amber-500/10" :
                rank === 2 ? "border-orange-400 bg-orange-50/60 dark:bg-orange-500/10" :
                "border-[#E5E5E5]";
              const badgeStyle =
                rank === 0 ? "bg-emerald-600 text-white" :
                rank === 1 ? "bg-amber-500 text-white" :
                rank === 2 ? "bg-orange-500 text-white" :
                "bg-neutral-200 text-neutral-700";
              const rankLabel =
                rank === 0 ? "1º · Melhor preço" :
                rank === 1 ? "2º lugar" :
                rank === 2 ? "3º lugar" : null;
              const labelStyle =
                rank === 0 ? "text-emerald-700" :
                rank === 1 ? "text-amber-700" :
                rank === 2 ? "text-orange-700" : "text-neutral-500";
              const wa = waUrl(
                g.supplier?.whatsapp,
                `Olá ${g.supplier?.name || ""}, tenho interesse em ${model.model_full}. Confirma o estoque e os preços?`,
              );
              return (
                <div key={key} className={`border rounded-md overflow-hidden ${rankStyle}`} data-testid={`sup-${gi}`}>
                  <button
                    type="button"
                    onClick={() => toggle(key)}
                    className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-neutral-50 dark:hover:bg-white/5 transition"
                    data-testid={`sup-toggle-${gi}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${badgeStyle}`}>
                        {gi + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {g.supplier?.name || "—"}
                          {g.supplier?.city && <span className="text-neutral-500 font-normal"> · {g.supplier.city}</span>}
                        </p>
                        <div className="flex items-center gap-2 text-[11px] text-neutral-500 mt-0.5 flex-wrap">
                          {g.supplier?.rating > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-amber-600"><Star size={10} fill="currentColor"/>{g.supplier.rating.toFixed(1)}</span>
                          )}
                          <span>{g.offer_count} variação(ões){g.total_qty > 0 && <> · {g.total_qty} un.</>}</span>
                          {rankLabel && <span className={`text-[10px] uppercase tracking-wider font-medium ${labelStyle}`}>{rankLabel}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        <p className="font-display text-lg tracking-tight">{brl(g.min_price)}</p>
                        {g.min_price !== g.max_price && (
                          <p className="text-[10px] text-neutral-500">até {brl(g.max_price)}</p>
                        )}
                      </div>
                      {wa && (
                        <a
                          href={wa} target="_blank" rel="noreferrer"
                          onClick={(e)=>e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`wa-${sid}`}
                        >
                          <MessageCircle size={12}/>
                        </a>
                      )}
                      <ChevronDown size={16} className={`text-neutral-400 transition-transform ${open ? "rotate-180" : ""}`}/>
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-neutral-200 bg-white dark:bg-transparent" data-testid={`sup-detail-${gi}`}>
                      <table className="w-full text-xs">
                        <thead className="bg-neutral-50">
                          <tr className="text-left">
                            <th className="p-2 font-medium text-neutral-600">Armazenamento</th>
                            <th className="p-2 font-medium text-neutral-600">Cor</th>
                            <th className="p-2 font-medium text-neutral-600 text-center">Qty</th>
                            <th className="p-2 font-medium text-neutral-600 text-right">Preço</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...g.items].sort((a, b) => a.price - b.price).map((it, i) => (
                            <tr key={`${i}-${it.color}-${it.storage}`} className="border-t border-neutral-100">
                              <td className="p-2">{it.storage || <span className="text-neutral-400">—</span>}</td>
                              <td className="p-2">{it.color || <span className="text-neutral-400">—</span>}</td>
                              <td className="p-2 text-center text-neutral-600">{it.quantity ?? "—"}</td>
                              <td className="p-2 text-right font-medium">{brl(it.price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      );})}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SUPPLIERS
// ─────────────────────────────────────────────────────────────────────────────
function SuppliersPane() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // {} for new, supplier for edit
  const [diagFor, setDiagFor] = useState(null); // supplier whose parsed products are being viewed
  const load = async () => {
    try {
      const r = await api.get("/compare/suppliers", { _cache: false });
      setList(r.data || []);
    } catch (e) { toast.error("Erro ao listar fornecedores"); }
  };
  useEffect(() => { load(); }, []);

  // Polling automático enquanto houver fornecedor com parse pendente em background
  useEffect(() => {
    const hasPending = list.some(s => s.parsed_summary && s.parsed_summary.pending);
    if (!hasPending) return;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [list]);

  const toggle = async (s) => {
    try {
      await api.put(`/compare/suppliers/${s.id}`, { active: !s.active });
      load();
    } catch (e) { toast.error("Erro"); }
  };
  const del = async (s) => {
    if (!window.confirm(`Excluir fornecedor "${s.name}" e suas listas? Esta ação não pode ser desfeita.`)) return;
    try { await api.delete(`/compare/suppliers/${s.id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error("Erro"); }
  };
  const reparse = async (s) => {
    try {
      const r = await api.post(`/compare/suppliers/${s.id}/parse`);
      const sm = r.data.summary || {};
      toast.success(`Lista reprocessada: ${sm.total} produto(s) (IA: ${sm.ai}, regex: ${sm.regex}, parcial: ${sm.partial})`);
      load();
    } catch (e) { toast.error("Erro ao processar"); }
  };

  const [reparsingAll, setReparsingAll] = useState(false);
  const reparseAll = async () => {
    if (!window.confirm(
      `Atualizar TODAS as listas (${list.length} fornecedor(es))?\n\n` +
      "Útil quando o parser foi atualizado. Cada lista será reprocessada com a versão mais recente.\n\n" +
      "Pode levar alguns segundos."
    )) return;
    setReparsingAll(true);
    try {
      const r = await api.post("/compare/suppliers/reparse-all");
      toast.success(`${r.data.total} fornecedor(es) atualizados`);
      load();
    } catch (e) {
      toast.error("Erro ao atualizar listas");
    } finally {
      setReparsingAll(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <p className="text-sm text-neutral-500">{list.length} fornecedor(es)</p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={reparseAll}
            disabled={reparsingAll || list.length === 0}
            title="Reaplica o parser mais recente em TODAS as listas"
            data-testid="reparse-all-button"
          >
            <Brain size={14} className="mr-1"/>
            {reparsingAll ? "Atualizando…" : "Atualizar listas"}
          </Button>
          <Button onClick={() => setEditing({})} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="new-supplier-button"><Plus size={14} className="mr-1"/>Novo fornecedor</Button>
        </div>
      </div>

      <div className="space-y-2">
        {list.map(s => (
          <Card key={s.id} className={`p-4 border-[#E5E5E5] shadow-none ${!s.active ? "opacity-60" : ""}`} data-testid={`supplier-${s.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-lg tracking-tight">{s.name} {s.rating > 0 && <span className="text-xs text-amber-600 ml-1">{"★".repeat(Math.round(s.rating))}</span>}</h3>
                  {s.internal_code && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-neutral-900 text-white font-mono">
                      cód {s.internal_code}
                    </span>
                  )}
                  {s.condition === "new" && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-medium">Novos</span>
                  )}
                  {s.condition === "used" && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-medium">Seminovos</span>
                  )}
                </div>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {s.city && <>{s.city} · </>}
                  {s.whatsapp || "sem WhatsApp"}
                  {s.last_list_update && <> · atualizado {new Date(s.last_list_update).toLocaleDateString("pt-BR")}</>}
                </p>
                {s.parsed_summary && s.parsed_summary.pending && (
                  <p className="text-[11px] text-amber-700 mt-1 inline-flex items-center gap-1.5" data-testid={`pending-${s.id}`}>
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse"/>
                    Processando lista em background…
                  </p>
                )}
                {s.parsed_summary && s.parsed_summary.total > 0 && !s.parsed_summary.pending && (
                  <p className="text-[11px] text-neutral-600 mt-1">
                    {s.parsed_summary.total} produtos · regex {s.parsed_summary.regex} · IA {s.parsed_summary.ai} · parcial {s.parsed_summary.partial}
                  </p>
                )}
                {s.note && <p className="text-[11px] text-neutral-500 italic mt-1">"{s.note}"</p>}
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <Button size="sm" variant="outline" onClick={()=>setDiagFor(s)} title="Ver produtos parseados" data-testid={`diag-${s.id}`}><BookOpen size={12} className="mr-1"/>Diagnóstico</Button>
                <Button size="sm" variant="outline" onClick={()=>reparse(s)} title="Reprocessar lista"><Brain size={12} className="mr-1"/>Reprocessar</Button>
                <Button size="sm" variant="outline" onClick={()=>setEditing(s)} data-testid={`edit-${s.id}`}><Edit2 size={12} className="mr-1"/>Editar</Button>
                <Button size="sm" variant="outline" onClick={()=>toggle(s)} className={s.active ? "" : "text-emerald-600"} data-testid={`toggle-${s.id}`}><Power size={12} className="mr-1"/>{s.active ? "Desativar" : "Ativar"}</Button>
                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={()=>del(s)} data-testid={`del-${s.id}`}><Trash2 size={12}/></Button>
              </div>
            </div>
          </Card>
        ))}
        {list.length === 0 && <p className="text-sm text-neutral-500 text-center py-10">Nenhum fornecedor ainda.</p>}
      </div>

      <SupplierDialog editing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>
      <DiagnosticDialog supplier={diagFor} onClose={() => setDiagFor(null)}/>
    </div>
  );
}

function DiagnosticDialog({ supplier, onClose }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!supplier) return;
    setLoading(true);
    api.get(`/compare/suppliers/${supplier.id}/products`, { _cache: false })
      .then(r => setProducts(r.data || []))
      .catch(()=>setProducts([]))
      .finally(()=>setLoading(false));
  }, [supplier]);
  if (!supplier) return null;
  // Agrupa por status do parsing
  const ok = products.filter(p => p.source === "regex" || p.source === "ai");
  const partial = products.filter(p => p.source === "partial");
  return (
    <Dialog open={!!supplier} onOpenChange={(v)=>!v && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-auto sm:max-w-3xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="diag-dialog">
        <DialogHeader>
          <DialogTitle>Diagnóstico — {supplier.name}</DialogTitle>
          <p className="text-xs text-neutral-500 mt-1">
            Veja como o sistema interpretou cada linha da lista colada. Itens com campos faltando aparecem em <span className="text-amber-700 font-medium">amber</span> — quanto mais detalhe, melhor a comparação.
          </p>
        </DialogHeader>
        {loading && <p className="text-sm text-neutral-500 text-center py-6">Carregando…</p>}
        {!loading && products.length === 0 && (
          <p className="text-sm text-neutral-500 text-center py-6">Nenhum produto parseado. Edite o fornecedor e cole a lista.</p>
        )}
        {!loading && products.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-neutral-500">{products.length} item(ns) — {ok.length} completos · {partial.length} incompletos</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-neutral-50">
                  <tr className="text-left">
                    <th className="p-2">Modelo</th>
                    <th className="p-2">Armaz.</th>
                    <th className="p-2">Cor</th>
                    <th className="p-2">Qty</th>
                    <th className="p-2 text-right">Preço</th>
                    <th className="p-2">Origem</th>
                    <th className="p-2">Raw</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => (
                    <tr key={p.id} className={`border-t border-neutral-100 ${p.source === "partial" ? "bg-amber-50" : ""}`}>
                      <td className="p-2 font-medium">{p.model_full || <span className="text-red-600">—</span>}</td>
                      <td className="p-2">{p.storage || <span className="text-amber-600">?</span>}</td>
                      <td className="p-2">{p.color || <span className="text-amber-600">?</span>}</td>
                      <td className="p-2">{p.quantity ?? "—"}</td>
                      <td className="p-2 text-right">{p.price ? `R$ ${Number(p.price).toLocaleString("pt-BR")}` : <span className="text-red-600">—</span>}</td>
                      <td className="p-2">
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${p.source === "ai" ? "bg-violet-100 text-violet-700" : p.source === "regex" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {p.source}
                        </span>
                      </td>
                      <td className="p-2 text-neutral-500 truncate max-w-[200px]" title={p.raw_text}>{p.raw_text}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {partial.length > 0 && (
              <div className="text-xs p-3 rounded bg-amber-50 border border-amber-200 text-amber-900 mt-2">
                💡 <strong>Dica:</strong> Itens em amber estão sem armazenamento/cor identificados. Eles ainda aparecem na comparação como "Sem detalhes específicos", mas pra agrupar com outros fornecedores edite a lista pra ficar mais detalhada, ou cadastre aliases no dicionário.
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SupplierDialog({ editing, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", whatsapp: "", city: "", note: "", rating: 0, active: true, raw_list: "", condition: "all", internal_code: "" });
  const [busy, setBusy] = useState(false);
  const isEdit = !!(editing && editing.id);
  useEffect(() => {
    if (editing && editing.id) {
      setForm({
        name: editing.name || "", whatsapp: editing.whatsapp || "",
        city: editing.city || "", note: editing.note || "",
        rating: editing.rating || 0, active: editing.active !== false,
        raw_list: editing.raw_list || "",
        condition: editing.condition || "all",
        internal_code: editing.internal_code || "",
      });
    } else if (editing) {
      setForm({ name: "", whatsapp: "", city: "", note: "", rating: 0, active: true, raw_list: "", condition: "all", internal_code: "" });
    }
  }, [editing]);

  if (!editing) return null;

  const save = async () => {
    if (busy) return;                              // anti double-click
    if (!form.name.trim()) { toast.error("Nome é obrigatório"); return; }
    setBusy(true);
    try {
      if (isEdit) {
        const r = await api.put(`/compare/suppliers/${editing.id}`, form);
        const sm = r.data?.parsed_summary || {};
        if (sm.pending) {
          toast.success("Salvo. Lista sendo processada em background…");
        } else {
          toast.success(`Salvo. ${sm.total ?? 0} produtos processados.`);
        }
      } else {
        const r = await api.post("/compare/suppliers", form);
        const sm = r.data?.parsed_summary || {};
        if (sm.pending) {
          toast.success("Criado. Lista sendo processada em background…");
        } else {
          toast.success(`Criado. ${sm.total ?? 0} produtos processados.`);
        }
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(v)=>!v && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-auto sm:max-w-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="supplier-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar fornecedor" : "Novo fornecedor"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Nome *</Label><Input value={form.name} onChange={(e)=>setForm(p=>({...p, name: e.target.value}))} data-testid="sup-name"/></div>
            <div>
              <Label>WhatsApp</Label>
              <Input
                value={form.whatsapp}
                onChange={(e)=>setForm(p=>({...p, whatsapp: e.target.value}))}
                placeholder="11 99999-9999 (BR) ou +1 555..."
                data-testid="sup-whatsapp-input"
              />
              <p className="text-[10px] text-neutral-500 mt-1">
                Sem o "+" assumimos Brasil (55). Com "+" preserva o DDI internacional.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Cidade</Label><Input value={form.city} onChange={(e)=>setForm(p=>({...p, city: e.target.value}))}/></div>
            <div>
              <Label>Nota (estrelas)</Label>
              <div className="flex items-center gap-1 mt-1">
                {[1,2,3,4,5].map(n => (
                  <button key={n} type="button" onClick={()=>setForm(p=>({...p, rating: n === p.rating ? 0 : n}))}
                    className={`text-2xl ${form.rating >= n ? "text-amber-500" : "text-neutral-300"} hover:scale-110 transition`}
                    data-testid={`rating-${n}`}>★</button>
                ))}
              </div>
            </div>
          </div>
          <div><Label>Observação</Label><Textarea rows={2} value={form.note} onChange={(e)=>setForm(p=>({...p, note: e.target.value}))} placeholder="Ex.: entrega rápida, parcela em 3x, etc."/></div>

          <div>
            <Label className="flex items-center gap-2">Esta lista é de Novos ou Semi-novos?</Label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="sup-condition-group">
              {[
                { v: "all", label: "Todos", hint: "Lista mista (novos e seminovos)" },
                { v: "new", label: "Novos", hint: "Apenas produtos novos / lacrados" },
                { v: "used", label: "Seminovos", hint: "Apenas seminovos / usados" },
              ].map(opt => {
                const active = (form.condition || "all") === opt.v;
                return (
                  <button
                    key={opt.v} type="button"
                    onClick={() => setForm(p => ({...p, condition: opt.v}))}
                    className={`text-left px-3 py-2 rounded-md border transition ${active
                      ? "border-emerald-500 bg-emerald-50/70 dark:bg-emerald-500/10"
                      : "border-[#E5E5E5] hover:border-neutral-400"}`}
                    data-testid={`sup-cond-${opt.v}`}
                  >
                    <span className={`text-sm font-medium ${active ? "text-emerald-700" : ""}`}>
                      {active ? "● " : "○ "}{opt.label}
                    </span>
                    <span className="block text-[10px] text-neutral-500">{opt.hint}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-neutral-500 mt-1.5">
              Use isto pra separar listas de novos e seminovos. Se o fornecedor manda os dois juntos, cadastre <strong>duas vezes</strong>.
            </p>
          </div>

          <div>
            <Label className="flex items-center gap-2">
              Código referência interna
              <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-normal">opcional</span>
            </Label>
            <Input
              value={form.internal_code}
              onChange={(e)=>setForm(p=>({...p, internal_code: e.target.value.slice(0, 20)}))}
              placeholder="Ex.: F1, A2, K-9, BR-01"
              maxLength={20}
              data-testid="sup-internal-code"
            />
            <p className="text-[11px] text-amber-700 mt-1.5 bg-amber-50/70 dark:bg-amber-500/10 border border-amber-200 rounded px-2 py-1.5">
              ⚠ <strong>Não coloque nome aqui.</strong> Esta informação é usada na função
              "Fazer repasse de lista" para você identificar internamente de qual fornecedor é o aparelho —
              o destinatário da mensagem NÃO descobre o nome do fornecedor, só vê o código.
            </p>
          </div>

          <div>
            <Label className="flex items-center gap-2">
              <Wand2 size={14} className="text-violet-600"/> Lista de produtos (cole o texto da lista)
            </Label>
            <Textarea
              rows={10}
              value={form.raw_list}
              onChange={(e)=>setForm(p=>({...p, raw_list: e.target.value}))}
              className="font-mono text-xs"
              placeholder={`Cole a lista do fornecedor aqui, em qualquer formato. Exemplos:\n\nIPHONE 17 PRO MAX 256GB\n10 BLUE\n10 SILVER\nR$ 8.200\n\n17PM 256 AZUL R$ 8500`}
              data-testid="sup-rawlist"
            />
            <p className="text-[11px] text-neutral-500 mt-1">
              <Sparkles size={11} className="inline mr-0.5"/> O sistema interpreta abreviações automaticamente (17PM, blk, lav, 256, etc.) e usa IA quando não reconhece.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button
            onClick={save}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="sup-save"
          >
            {busy ? "Salvando…" : (isEdit ? "Salvar" : "Criar e processar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ALIASES (dicionário)
// ─────────────────────────────────────────────────────────────────────────────
function AliasesPane() {
  const [list, setList] = useState([]);
  const [src, setSrc] = useState("");
  const [tgt, setTgt] = useState("");
  const load = async () => {
    try { const r = await api.get("/compare/aliases", { _cache: false }); setList(r.data || []); }
    catch {}
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!src.trim() || !tgt.trim()) return;
    try { await api.post("/compare/aliases", { source: src.trim(), target: tgt.trim() }); setSrc(""); setTgt(""); load(); toast.success("Adicionado"); }
    catch (e) { toast.error("Erro"); }
  };
  const del = async (a) => {
    try { await api.delete(`/compare/aliases/${a.id}`); load(); } catch {}
  };
  return (
    <div className="space-y-4">
      <Card className="p-4 border-[#E5E5E5] shadow-none">
        <Label className="flex items-center gap-2"><BookOpen size={14}/> Dicionário de aliases (aprendizado)</Label>
        <p className="text-xs text-neutral-500 mt-1">
          O sistema já reconhece 17PM, blk, lav, pm, etc. Adicione aqui seus próprios atalhos — eles são aplicados em TODAS as importações.
        </p>
        <div className="flex gap-2 mt-3 flex-wrap">
          <Input value={src} onChange={(e)=>setSrc(e.target.value)} placeholder="Abreviação (ex.: 17pm)" className="flex-1 min-w-[140px]" data-testid="alias-src"/>
          <span className="self-center text-neutral-400">→</span>
          <Input value={tgt} onChange={(e)=>setTgt(e.target.value)} placeholder="Forma completa (ex.: iPhone 17 Pro Max)" className="flex-1 min-w-[200px]" data-testid="alias-tgt"/>
          <Button onClick={add} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="alias-add"><Plus size={14} className="mr-1"/>Adicionar</Button>
        </div>
      </Card>
      <div className="space-y-1.5">
        {list.map(a => (
          <div key={a.id} className="flex items-center justify-between gap-2 p-2.5 border border-[#E5E5E5] rounded-md" data-testid={`alias-${a.id}`}>
            <div className="text-sm">
              <span className="font-mono px-1.5 py-0.5 rounded bg-neutral-100">{a.source}</span>
              <span className="mx-2 text-neutral-400">→</span>
              <span className="font-medium">{a.target}</span>
              {a.learned && <span className="ml-2 text-[10px] uppercase tracking-wider text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">Aprendido</span>}
            </div>
            <button onClick={()=>del(a)} className="text-red-500 hover:bg-red-50 p-1 rounded" title="Excluir"><X size={14}/></button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-neutral-500 text-center py-6">Nenhum alias customizado. O dicionário base interno já cobre os casos comuns.</p>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RECIPIENTS — enviar a tabela formatada por WhatsApp
// ─────────────────────────────────────────────────────────────────────────────
function RecipientsPane() {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [rows, setRows] = useState([]);
  // Filtros e configuração de envio
  const [condFilter, setCondFilter] = useState("all"); // all|new|used
  const [repasse, setRepasse] = useState(false);       // ativa "fazer repasse"
  const [repValue, setRepValue] = useState("");        // valor numérico
  const [repMode, setRepMode] = useState("brl");       // brl | pct

  const load = async () => {
    try {
      const [r, c] = await Promise.all([
        api.get("/compare/recipients", { _cache: false }),
        api.get("/compare/comparison", { _cache: false }),
      ]);
      setList(r.data || []);
      setRows(c.data || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim() || !phone.trim()) return;
    try { await api.post("/compare/recipients", { name, phone }); setName(""); setPhone(""); load(); toast.success("Adicionado"); }
    catch { toast.error("Erro"); }
  };
  const del = async (r) => {
    try { await api.delete(`/compare/recipients/${r.id}`); load(); } catch {}
  };

  // Filtra suppliers por condição (igual ao ComparePane)
  const passesCondition = (cond) => {
    if (condFilter === "all") return true;
    return cond === condFilter || cond === "all";
  };

  // Calcula preço final (com repasse aplicado)
  const finalPrice = (basePrice) => {
    if (!repasse) return basePrice;
    const raw = parseFloat((repValue || "").toString().replace(",", "."));
    if (isNaN(raw) || raw <= 0) return basePrice;
    return repMode === "pct" ? basePrice * (1 + raw / 100) : basePrice + raw;
  };

  const titleByFilter = {
    all: "Lista de Novos e Semi novos",
    new: "Lista Apple Novos",
    used: "Lista Apple Semi-novos",
  };

  const buildMessage = () => {
    if (rows.length === 0) return "Nenhum produto na comparação.";
    // Em modo repasse, agrupamos por (model, storage, color) e usamos o MENOR
    // preço como base — depois aplicamos o acréscimo, escondemos fornecedor.
    const fmt = (v) => Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    let msg = repasse
      ? `📱 *${titleByFilter[condFilter]}*\n\n`
      : `📱 *Tabela de Preços* (${titleByFilter[condFilter]})\n\n`;

    if (repasse) {
      // ── MODO REPASSE ── agrupa por modelo+storage+color, sem fornecedor
      // mas mostra o internal_code do fornecedor que tem aquele menor preço.
      rows.forEach(model => {
        const variants = new Map(); // key=storage|color → {storage, color, minPrice, code}
        (model.suppliers || []).forEach(g => {
          if (!passesCondition(g.supplier?.condition || "all")) return;
          (g.items || []).forEach(it => {
            const key = `${it.storage || ""}__${it.color || ""}`;
            const cur = variants.get(key);
            if (!cur || it.price < cur.price) {
              variants.set(key, {
                storage: it.storage, color: it.color, price: it.price,
                code: g.supplier?.internal_code || "",
              });
            }
          });
        });
        if (variants.size === 0) return;
        msg += `*${model.model_full}*\n`;
        [...variants.values()]
          .sort((a, b) => (a.storage || "").localeCompare(b.storage || ""))
          .forEach(v => {
            const partsLeft = [v.storage, v.color].filter(Boolean).join(" · ") || "—";
            const codeTag = v.code ? ` [${v.code}]` : "";
            msg += `   ${partsLeft} → R$ ${fmt(finalPrice(v.price))}${codeTag}\n`;
          });
        msg += `\n`;
      });
    } else {
      // ── MODO PADRÃO ── lista por fornecedor com ranking 🟢🟡🔴
      rows.forEach(model => {
        const suppliers = (model.suppliers || [])
          .filter(g => passesCondition(g.supplier?.condition || "all"));
        if (suppliers.length === 0) return;
        msg += `*${model.model_full}*\n`;
        suppliers.forEach((g, gi) => {
          const rankIcon = gi === 0 ? "🟢" : gi === 1 ? "🟡" : gi === 2 ? "🔴" : "•";
          const supName = g.supplier?.name || "—";
          const items = (g.items || []).slice().sort((a, b) => (a.storage || "").localeCompare(b.storage || ""));
          if (items.length === 0) return;
          msg += `${rankIcon} *${supName}*`;
          if (g.supplier?.city) msg += ` (${g.supplier.city})`;
          msg += `\n`;
          items.forEach(it => {
            const partsLeft = [it.storage, it.color].filter(Boolean).join(" · ") || "—";
            const qty = it.quantity != null ? ` × ${it.quantity}` : "";
            msg += `   ${partsLeft}${qty} → R$ ${fmt(it.price)}\n`;
          });
        });
        msg += `\n`;
      });
    }
    msg += `_Atualizado em ${new Date().toLocaleDateString("pt-BR")} ${new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}_`;
    return msg;
  };

  const buildWaUrl = (recipient) => waUrl(recipient.phone, buildMessage());

  const [preview, setPreview] = useState(false);
  const previewText = useMemo(() => buildMessage(), [rows, condFilter, repasse, repValue, repMode]); // eslint-disable-line react-hooks/exhaustive-deps
  const copyPreview = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(previewText);
      } else {
        // Fallback pra contextos sem Clipboard API (iframe, http inseguro, etc.)
        const ta = document.createElement("textarea");
        ta.value = previewText;
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Tabela copiada");
    } catch {
      toast.error("Não foi possível copiar — selecione manualmente");
    }
  };
  const totalProducts = useMemo(
    () => rows.reduce((acc, m) => acc + (m.suppliers || []).reduce((s, g) => s + (g.items?.length || 0), 0), 0),
    [rows],
  );

  return (
    <div className="space-y-4">
      <Card className="p-4 border-[#E5E5E5] shadow-none">
        <Label className="flex items-center gap-2"><Send size={14}/> Cadastrar número para receber lista</Label>
        <div className="flex gap-2 mt-3 flex-wrap">
          <Input value={name} onChange={(e)=>setName(e.target.value)} placeholder="Nome" className="flex-1 min-w-[140px]" data-testid="rec-name"/>
          <Input value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="WhatsApp" className="flex-1 min-w-[140px]" data-testid="rec-phone"/>
          <Button onClick={add} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="rec-add"><Plus size={14} className="mr-1"/>Adicionar</Button>
        </div>
      </Card>

      <Card className="p-4 border-[#E5E5E5] shadow-none" data-testid="send-options">
        <Label className="flex items-center gap-2 mb-2"><Sparkles size={14}/> Opções de envio</Label>

        {/* Filtro de condição */}
        <div className="mb-3">
          <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Filtrar produtos</p>
          <div className="flex flex-wrap gap-2">
            {[
              { v: "all", label: "Todos", hint: "Novos + Semi-novos" },
              { v: "new", label: "Novos", hint: "Só fornecedores de novos" },
              { v: "used", label: "Semi-novos", hint: "Só fornecedores de semi-novos" },
            ].map(opt => {
              const on = condFilter === opt.v;
              return (
                <button
                  key={opt.v} type="button"
                  onClick={() => setCondFilter(opt.v)}
                  className={`text-left px-3 py-1.5 rounded-md border transition text-sm ${on
                    ? "border-emerald-500 bg-emerald-50/70 dark:bg-emerald-500/10 text-emerald-800"
                    : "border-[#E5E5E5] hover:border-neutral-400"}`}
                  data-testid={`send-filter-${opt.v}`}
                >
                  {on ? "● " : "○ "}{opt.label}
                  <span className="block text-[10px] text-neutral-500 font-normal">{opt.hint}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Repasse */}
        <div className="border-t border-neutral-200 dark:border-white/10 pt-3 mt-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox" checked={repasse} onChange={(e)=>setRepasse(e.target.checked)}
              className="accent-emerald-600" data-testid="send-repasse-toggle"
            />
            <span className="text-sm font-medium">Fazer repasse da lista</span>
          </label>
          <p className="text-[11px] text-neutral-500 mt-1">
            Acrescenta um valor em cima de cada produto. Esconde o nome dos fornecedores. Útil pra ganhar margem revendendo a lista.
          </p>
          {repasse && (
            <div className="flex items-end gap-2 mt-3 flex-wrap">
              <div>
                <Label className="text-[11px]">Valor a acrescentar</Label>
                <Input
                  type="text" inputMode="decimal"
                  value={repValue} onChange={(e)=>setRepValue(e.target.value)}
                  placeholder={repMode === "pct" ? "Ex.: 5" : "Ex.: 100,00"}
                  className="w-40" data-testid="send-repasse-value"
                />
              </div>
              <div className="flex gap-1">
                <button
                  type="button" onClick={()=>setRepMode("brl")}
                  className={`px-3 py-2 text-sm rounded-md border ${repMode === "brl"
                    ? "border-emerald-500 bg-emerald-50/70 text-emerald-700"
                    : "border-[#E5E5E5]"}`}
                  data-testid="send-repasse-brl"
                >R$ fixo</button>
                <button
                  type="button" onClick={()=>setRepMode("pct")}
                  className={`px-3 py-2 text-sm rounded-md border ${repMode === "pct"
                    ? "border-emerald-500 bg-emerald-50/70 text-emerald-700"
                    : "border-[#E5E5E5]"}`}
                  data-testid="send-repasse-pct"
                >% sobre o preço</button>
              </div>
              <p className="text-[10px] text-neutral-500 self-center">Ex.: R$ 100 ou 5%</p>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-4 border-[#E5E5E5] shadow-none">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <Label className="flex items-center gap-2"><Sparkles size={14}/> Pré-visualizar a mensagem</Label>
            <p className="text-[11px] text-neutral-500 mt-1">
              {rows.length} modelo(s) · {totalProducts} produto(s)
              {repasse && <> · <span className="text-emerald-700 font-medium">modo repasse (sem fornecedor)</span></>}
              {condFilter !== "all" && <> · filtro <span className="font-medium">{condFilter === "new" ? "Novos" : "Semi-novos"}</span></>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={()=>setPreview(p=>!p)} data-testid="preview-toggle">{preview ? "Ocultar" : "Ver prévia"}</Button>
            <Button size="sm" onClick={copyPreview} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="copy-preview"><BookOpen size={12} className="mr-1"/>Copiar</Button>
          </div>
        </div>
        {preview && (
          <Textarea
            value={previewText} readOnly rows={Math.min(20, previewText.split("\n").length + 1)}
            className="mt-3 font-mono text-[11px] whitespace-pre"
            data-testid="preview-textarea"
          />
        )}
      </Card>

      <div className="space-y-2">
        {list.map(r => (
          <div key={r.id} className="flex items-center justify-between gap-2 p-3 border border-[#E5E5E5] rounded-md" data-testid={`rec-${r.id}`}>
            <div>
              <p className="text-sm font-medium">{r.name}</p>
              <p className="text-[11px] text-neutral-500">{r.phone}</p>
            </div>
            <div className="flex items-center gap-1">
              <a
                href={buildWaUrl(r) || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm bg-emerald-600 hover:bg-emerald-700 text-white ${!buildWaUrl(r) ? "opacity-50 pointer-events-none" : ""}`}
                data-testid={`send-${r.id}`}
              >
                <Send size={12} className="mr-1"/>Enviar lista
              </a>
              <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={()=>del(r)}><Trash2 size={12}/></Button>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-neutral-500 text-center py-6">Cadastre destinatários acima para enviar a tabela com 1 clique via WhatsApp.</p>}
      </div>
    </div>
  );
}
